import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  description?: string;
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  undo: (title: string, description: string, onUndo: () => void, undoLabel?: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

interface ToastProviderProps {
  children: React.ReactNode;
}

const iconMap = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
};

const colorMap = {
  success: { accent: 'var(--accent-success)', bg: 'var(--accent-success-bg)', text: '#047857' },
  error: { accent: 'var(--accent-error)', bg: 'var(--accent-error-bg)', text: '#B91C1C' },
  info: { accent: 'var(--accent-primary)', bg: 'var(--accent-primary-bg)', text: 'var(--accent-primary-active)' },
  warning: { accent: 'var(--accent-warning)', bg: 'var(--accent-warning-bg)', text: '#B45309' },
};

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const addToast = React.useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 11);
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = React.useCallback(
    (title: string, description?: string) => {
      addToast({ type: 'success', title, description, duration: 4000 });
    },
    [addToast]
  );

  const error = React.useCallback(
    (title: string, description?: string) => {
      addToast({ type: 'error', title, description, duration: 6000 });
    },
    [addToast]
  );

  const info = React.useCallback(
    (title: string, description?: string) => {
      addToast({ type: 'info', title, description, duration: 4000 });
    },
    [addToast]
  );

  const warning = React.useCallback(
    (title: string, description?: string) => {
      addToast({ type: 'warning', title, description, duration: 5000 });
    },
    [addToast]
  );

  const undo = React.useCallback(
    (title: string, description: string, onUndo: () => void, undoLabel?: string) => {
      addToast({
        type: 'warning',
        title,
        description,
        duration: 7000,
        action: { label: undoLabel ?? 'Undo', onClick: onUndo },
      });
    },
    [addToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, info, warning, undo }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map((toast) => {
          const Icon = iconMap[toast.type];
          const colors = colorMap[toast.type];

          return (
            <ToastPrimitive.Root
              key={toast.id}
              duration={toast.duration}
              onOpenChange={(open) => {
                if (!open) removeToast(toast.id);
              }}
              style={{
                background: 'var(--bg-primary)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-xl)',
                padding: '16px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                maxWidth: '400px',
                border: '1px solid var(--border-primary)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Accent line */}
              <div
                style={{
                  position: 'absolute',
                  insetInlineStart: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  background: colors.accent,
                  borderStartStartRadius: 'var(--radius-lg)',
                  borderEndStartRadius: 'var(--radius-lg)',
                }}
              />

              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: 'var(--radius-md)',
                  background: colors.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon style={{ width: '18px', height: '18px', color: colors.accent }} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <ToastPrimitive.Title
                  style={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    margin: 0,
                    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  }}
                >
                  {toast.title}
                </ToastPrimitive.Title>
                {toast.description && (
                  <ToastPrimitive.Description
                    style={{
                      fontSize: '13px',
                      color: 'var(--text-tertiary)',
                      marginTop: '4px',
                    }}
                  >
                    {toast.description}
                  </ToastPrimitive.Description>
                )}
                {toast.action && (
                  <ToastPrimitive.Action altText={toast.action.label} asChild>
                    <button
                      onClick={() => {
                        toast.action!.onClick();
                        removeToast(toast.id);
                      }}
                      style={{
                        marginTop: '8px',
                        padding: '4px 12px',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: colors.accent,
                        background: colors.bg,
                        border: `1px solid ${colors.accent}30`,
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        fontFamily: "'Inter', system-ui, sans-serif",
                        transition: `background var(--motion-duration-micro) var(--motion-ease-standard)`,
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.outline = '2px solid var(--border-focus)';
                        e.currentTarget.style.outlineOffset = '2px';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.outline = 'none';
                      }}
                    >
                      {toast.action.label}
                    </button>
                  </ToastPrimitive.Action>
                )}
              </div>

              <ToastPrimitive.Close
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-muted)',
                  flexShrink: 0,
                  transition: `background var(--motion-duration-fast) var(--motion-ease-standard)`,
                }}
                onFocus={(e) => {
                  e.currentTarget.style.outline = '2px solid var(--border-focus)';
                  e.currentTarget.style.outlineOffset = '2px';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.outline = 'none';
                }}
                aria-label="Close"
              >
                <X style={{ width: '16px', height: '16px' }} />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}
        <ToastPrimitive.Viewport
          style={{
            position: 'fixed',
            bottom: '24px',
            insetInlineEnd: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            zIndex: 9999,
            maxWidth: '100vw',
            padding: '0 16px',
          }}
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}
